import { Button, Flex, Heading, Text } from '@chakra-ui/react';
import { isSameDay } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';

import { AddToCalendarSession } from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendarSession';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { ThinEventSession } from '@/resources/ThinEventSession';

interface ISessionCard {
  session: ThinEventSession;
}
export const SessionCard: FC<ISessionCard> = ({ session }) => {
  const router = useRouter();
  const { timeZone, eventId } = useQueryParams<{ timeZone: string; eventId: string }>({
    timeZone: StringParam,
    eventId: StringParam,
  });

  const { startsAt, endsAt } = session;

  const timeStart = useMemo(
    () =>
      isSameDay(utcToZonedTime(startsAt, timeZone), new Date())
        ? formatInTimeZone(startsAt, timeZone, 'HH:mm')
        : formatInTimeZone(startsAt, timeZone, 'LL/dd/yyyy HH:mm'),
    [startsAt, timeZone]
  );
  const timeEnd = useMemo(
    () =>
      isSameDay(utcToZonedTime(endsAt, timeZone), new Date())
        ? formatInTimeZone(endsAt, timeZone, 'HH:mm')
        : formatInTimeZone(endsAt, timeZone, 'LL/dd/yyyy HH:mm'),
    [endsAt, timeZone]
  );

  function handleSessionCardClick() {
    const isPoster = session.posterOnly;
    const finalUrl =
      session.url || `/events/${eventId}/${isPoster ? 'posters' : 'sessions'}?eventSessionId=${session.id}`;

    router.push(finalUrl);
  }

  return (
    <Flex
      onClick={handleSessionCardClick}
      cursor="pointer"
      flexDir="column"
      bgColor="white"
      p={4}
      borderColor="gray.100"
      borderWidth="1px"
    >
      <Flex justifyContent="space-between" position="relative" mb={2}>
        <Heading fontSize="md" noOfLines={2} size="sm">
          {session.name}
        </Heading>
        <AddToCalendarSession color="black" timeZone={timeZone} session={session} />
      </Flex>
      <Flex justifyContent="space-between" alignItems="flex-end">
        <Flex fontSize="sm" flexDir="column" w="full">
          <Text>
            {timeStart} - {timeEnd}
          </Text>
          <Text style={{ lineBreak: 'anywhere' }} w="full" noOfLines={4} mt={2}>
            {session.venue}
          </Text>
        </Flex>
        {session.ctaLabel && session.ctaUrl && (
          <Button onClick={(e) => e.stopPropagation()} target="_blank" as={NextLink} href={session.ctaUrl} size="sm">
            {session.ctaLabel}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
